import { useState, useEffect, useCallback } from 'react';
import { ubigeoService } from '../service';
import type { Departamento, Provincia, Distrito, UbigeoSelection } from '../types';

export interface UseUbigeoCascadeReturn {
  selection: UbigeoSelection;
  departamentos: Departamento[];
  provincias: Provincia[];
  distritos: Distrito[];
  esCargando: boolean;
  cargandoDepartamentos: boolean;
  cargandoProvincias: boolean;
  cargandoDistritos: boolean;
  setDepartamento: (departamento: Departamento | null) => void;
  setProvincia: (provincia: Provincia | null) => void;
  setDistrito: (distrito: Distrito | null) => void;
  reset: () => void;
  error: Error | null;
}

export function useUbigeoCascade(initialValue?: UbigeoSelection): UseUbigeoCascadeReturn {
  const [selection, setSelection] = useState<UbigeoSelection>(initialValue || {});
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [distritos, setDistritos] = useState<Distrito[]>([]);

  const [cargandoDepartamentos, setCargandoDepartamentos] = useState(false);
  const [cargandoProvincias, setCargandoProvincias] = useState(false);
  const [cargandoDistritos, setCargandoDistritos] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchDepartamentos = async () => {
      setCargandoDepartamentos(true);
      setError(null);
      try {
        const data = await ubigeoService.getDepartamentos();
        if (isMounted) {
          setDepartamentos(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Error al cargar departamentos'));
          setDepartamentos([]);
        }
      } finally {
        if (isMounted) {
          setCargandoDepartamentos(false);
        }
      }
    };

    fetchDepartamentos();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selection.departamento?.codigo) {
      setProvincias([]);
      setCargandoProvincias(false);
      return;
    }

    let isMounted = true;
    
    const fetchProvincias = async () => {
      setCargandoProvincias(true);
      setError(null);
      try {
        const data = await ubigeoService.getProvincias(selection.departamento!.codigo);
        if (isMounted) {
          setProvincias(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Error al cargar provincias'));
          setProvincias([]);
        }
      } finally {
        if (isMounted) {
          setCargandoProvincias(false);
        }
      }
    };

    fetchProvincias();

    return () => {
      isMounted = false;
    };
  }, [selection.departamento]);

  useEffect(() => {
    if (!selection.departamento?.codigo || !selection.provincia?.codigo) {
      setDistritos([]);
      setCargandoDistritos(false);
      return;
    }

    let isMounted = true;
    
    const fetchDistritos = async () => {
      setCargandoDistritos(true);
      setError(null);
      try {
        const data = await ubigeoService.getDistritos(
          selection.departamento!.codigo,
          selection.provincia!.codigo
        );
        if (isMounted) {
          setDistritos(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Error al cargar distritos'));
          setDistritos([]);
        }
      } finally {
        if (isMounted) {
          setCargandoDistritos(false);
        }
      }
    };

    fetchDistritos();

    return () => {
      isMounted = false;
    };
  }, [selection.departamento, selection.provincia]);

  useEffect(() => {
    if (initialValue) {
      setSelection(initialValue);
    }
  }, [initialValue]);

  const handleDepartamentoChange = useCallback((departamento: Departamento | null) => {
    setSelection({
      departamento: departamento || undefined,
      provincia: undefined,
      distrito: undefined
    });
  }, []);

  const handleProvinciaChange = useCallback((provincia: Provincia | null) => {
    setSelection(prev => ({
      ...prev,
      provincia: provincia || undefined,
      distrito: undefined
    }));
  }, []);

  const handleDistritoChange = useCallback((distrito: Distrito | null) => {
    setSelection(prev => ({
      ...prev,
      distrito: distrito || undefined
    }));
  }, []);

  const reset = useCallback(() => {
    setSelection({});
  }, []);

  const esCargando = cargandoDepartamentos || cargandoProvincias || cargandoDistritos;

  return {
    selection,
    departamentos,
    provincias,
    distritos,
    esCargando,
    cargandoDepartamentos,
    cargandoProvincias,
    cargandoDistritos,
    setDepartamento: handleDepartamentoChange,
    setProvincia: handleProvinciaChange,
    setDistrito: handleDistritoChange,
    reset,
    error
  };
}
